import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  BarChart3,
  Sparkles,
  Building2,
  Users2,
  CheckCircle2,
  PlayCircle,
  Sun,
  Moon,
  Database,
  LayoutDashboard,
  TrendingUp,
  School
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import GlassCard from "./ui/GlassCard";

// Internal Component: Abstract Dashboard Visualization
const DashboardMockup = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, rotateX: 10 }}
      animate={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
      className="relative w-full max-w-lg mx-auto lg:max-w-none perspective-1000"
    >
      {/* Main Window */}
      <div className="relative z-10 overflow-hidden rounded-2xl bg-white/40 dark:bg-slate-900/40 border border-white/20 dark:border-white/10 shadow-2xl backdrop-blur-xl">
        {/* Header Mockup */}
        <div className="flex items-center justify-between border-b border-white/10 p-4 bg-white/10 dark:bg-black/20">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-red-400/80" />
            <div className="h-3 w-3 rounded-full bg-yellow-400/80" />
            <div className="h-3 w-3 rounded-full bg-green-400/80" />
          </div>
          <div className="h-2 w-20 rounded-full bg-slate-400/20" />
        </div>

        {/* Content Mockup */}
        <div className="p-6 grid gap-6">
          {/* Top Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl bg-white/50 dark:bg-slate-800/50 p-4 shadow-sm border border-white/10">
              <div className="h-2 w-12 rounded bg-emerald-500/30 mb-2" />
              <div className="h-8 w-24 rounded bg-slate-900/10 dark:bg-white/10" />
            </div>
            <div className="rounded-xl bg-white/50 dark:bg-slate-800/50 p-4 shadow-sm border border-white/10">
              <div className="h-2 w-12 rounded bg-blue-500/30 mb-2" />
              <div className="h-8 w-24 rounded bg-slate-900/10 dark:bg-white/10" />
            </div>
          </div>

          {/* Chart Area */}
          <div className="rounded-xl bg-white/50 dark:bg-slate-800/50 p-4 h-40 shadow-sm border border-white/10 flex items-end justify-between gap-2">
            {[40, 70, 50, 90, 60, 80, 45].map((h, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                animate={{ height: `${h}%` }}
                transition={{ duration: 1, delay: 0.5 + i * 0.1 }}
                className="w-full rounded-t bg-emerald-500/60 dark:bg-emerald-400/60"
              />
            ))}
          </div>

          {/* Table Rows */}
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 rounded-lg bg-white/30 dark:bg-slate-800/30 flex items-center px-4 gap-4">
                <div className="h-2 w-2 rounded-full bg-slate-400/30" />
                <div className="h-2 w-full rounded bg-slate-400/10" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Decorative Floating Elements */}
      <motion.div
        animate={{ y: [0, -15, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -right-8 top-20 z-20 rounded-2xl bg-white dark:bg-slate-800 p-4 shadow-xl border border-emerald-100 dark:border-emerald-900"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-emerald-100 dark:bg-emerald-900/50">
            <TrendingUp className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Behörighet</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white">+4.2%</div>
          </div>
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute -left-8 bottom-20 z-20 rounded-2xl bg-white dark:bg-slate-800 p-4 shadow-xl border border-blue-100 dark:border-blue-900"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/50">
            <School className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Skolor</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white">Active</div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function DesktopLandingPage() {
  const { theme, toggle } = useTheme();

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      // Offset for sticky header if needed, but on mobile we hid it.
      // Desktop has sticky header.
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <>
      <link rel="preload" href="/logo.svg" as="image" type="image/svg+xml" />

      {/* JSON-LD for Search Engines */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "eduInsights",
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "SEK"
          },
          "description": "Beslutsstöd och dataanalys för skolförvaltning. Visualisera trender från Kolada eller SCB och kommunala system.",
          "featureList": "Nyckeltal, Jämförelser, Trender, Könsfördelning"
        })}
      </script>

      <main className="min-h-screen bg-background text-foreground pb-24 sm:pb-0 overflow-x-hidden selection:bg-emerald-100 selection:text-emerald-900" role="main">

        {/* Background Gradients */}
        <div className="fixed top-0 left-0 right-0 h-[800px] overflow-hidden pointer-events-none -z-10">
          <div className="absolute -top-20 -right-20 w-[600px] h-[600px] bg-emerald-400/10 dark:bg-emerald-500/5 blur-[100px] rounded-full mix-blend-multiply dark:mix-blend-screen animate-pulse" style={{ animationDuration: '8s' }} />
          <div className="absolute top-40 -left-20 w-[500px] h-[500px] bg-blue-400/10 dark:bg-blue-500/5 blur-[100px] rounded-full mix-blend-multiply dark:mix-blend-screen animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }} />
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden sm:block sticky top-0 z-40 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-white/20 dark:border-white/10">
          <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="relative">
                <div className="absolute inset-0 bg-emerald-500 blur-lg opacity-20 group-hover:opacity-40 transition-opacity" />
                <img src="/logo.svg" alt="Logo" className="h-8 w-8 relative z-10" />
              </div>
              <span className="font-bold text-lg tracking-tight">eduInsights</span>
            </Link>

            <div className="flex items-center gap-6">
              {/* Internal Anchors */}
              <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600 dark:text-slate-400">
                <button onClick={() => scrollToSection('features')} className="hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">Funktioner</button>
                <button onClick={() => scrollToSection('tech')} className="hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">Teknik</button>
                <button onClick={() => scrollToSection('faq')} className="hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">FAQ</button>
              </div>

              <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-2" />

              <button onClick={toggle} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" aria-label="Toggle Theme">
                {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </button>

              <Link
                to="/kommun"
                className="px-5 py-2 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-bold hover:shadow-lg hover:scale-105 transition-all active:scale-95"
              >
                Starta Appen
              </Link>
            </div>
          </div>
        </nav>

        {/* HERO SECTION */}
        <section className="relative pt-12 pb-20 lg:pt-24 lg:pb-32 px-6">
          <div className="mx-auto max-w-7xl grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Text Content */}
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 text-sm font-medium mb-6 border border-emerald-200/50 dark:border-emerald-800/50"
              >
                <Sparkles className="h-4 w-4" />
                <span>Smartare Skolanalys 2026</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-5xl lg:text-7xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-6 leading-[1.1]"
              >
                Från data till <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500 dark:from-emerald-400 dark:to-teal-300">beslut</span> på sekunder.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-lg lg:text-xl text-slate-600 dark:text-slate-300 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed"
              >
                Sluta leta i Excel-filer. Få en omedelbar överblick av skolans nyckeltal, trender och jämförelser – direkt i webbläsaren.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4"
              >
                <Link
                  to="/kommun"
                  className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold text-lg shadow-xl shadow-emerald-500/20 hover:shadow-emerald-500/40 hover:scale-105 transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  <LayoutDashboard className="h-5 w-5" />
                  Se Kommunöversikt
                </Link>
                <a
                  href="#features"
                  className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-bold text-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
                >
                  Läs mer
                </a>
              </motion.div>

              {/* Social Proof Text */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-10 pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6 text-sm text-slate-500 dark:text-slate-400"
              >
                <div className="flex -space-x-3">
                  <div className="w-10 h-10 rounded-full bg-slate-200 border-2 border-white dark:border-slate-900" />
                  <div className="w-10 h-10 rounded-full bg-slate-300 border-2 border-white dark:border-slate-900" />
                  <div className="w-10 h-10 rounded-full bg-emerald-100 border-2 border-white dark:border-slate-900 flex items-center justify-center font-bold text-emerald-700 text-xs">+2k</div>
                </div>
                <p>Nyckeltal från <span className="font-bold text-slate-700 dark:text-slate-200">Kolada</span> & <span className="font-bold text-slate-700 dark:text-slate-200">Skolverket</span></p>
              </motion.div>
            </div>

            {/* Visual Content */}
            <div className="relative">
              <DashboardMockup />
            </div>
          </div>
        </section>

        {/* FEATURES GRID */}
        <section id="features" className="py-24 bg-slate-50/50 dark:bg-slate-900/50 relative scroll-mt-20">
          <div className="mx-auto max-w-7xl px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 dark:text-white mb-4">Verktyg byggda för verkligheten</h2>
              <p className="text-lg text-slate-600 dark:text-slate-400">Designat för att ge politiker, tjänstepersoner och rektorer gemensamma svar på svåra frågor. En datadriven dialog.</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: <BarChart3 className="w-8 h-8 text-blue-500" />,
                  title: "Interaktiva Trender",
                  desc: "Visualisera utvecklingen över tid. Filtrera på år och se vart kurvorna pekar."
                },
                {
                  icon: <Users2 className="w-8 h-8 text-purple-500" />,
                  title: "Könsuppdelad Statistik",
                  desc: "Analysera skillnader mellan pojkar och flickor med ett klick. Jämlikhet i fokus."
                },
                {
                  icon: <Building2 className="w-8 h-8 text-emerald-500" />,
                  title: "Skoljämförelser",
                  desc: "Jämför skolor inom kommunen rättvist. Se 'Top 3' och 'Bottom 3' direkt."
                },
                {
                  icon: <Database className="w-8 h-8 text-amber-500" />,
                  title: "Öppna Data",
                  desc: "Bygger på officiell statistik från t.ex. Kolada eller SCB. Alltid uppdaterat, alltid transparent."
                },
                {
                  icon: <LayoutDashboard className="w-8 h-8 text-cyan-500" />,
                  title: "Responsiv Dashboard",
                  desc: "Fungerar lika bra på iPaden i nämndrummet som på mobilen i korridoren."
                },
                {
                  icon: <ShieldCheck className="w-8 h-8 text-rose-500" />,
                  title: "Trygg & Säker",
                  desc: "Inga personuppgifter lagras i onödan. GDPR-säkrad arkitektur från grunden."
                }
              ].map((f, i) => (
                <GlassCard key={i} className="p-8 hover:shadow-xl transition-shadow border-white/40 dark:border-white/5" hoverEffect>
                  <div className="mb-6 p-3 rounded-2xl bg-white dark:bg-slate-800 w-fit shadow-sm border border-slate-100 dark:border-slate-700">
                    {f.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{f.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{f.desc}</p>
                </GlassCard>
              ))}
            </div>
          </div>
        </section>

        {/* TECH & TRUST SECTION */}
        <section id="tech" className="py-24 px-6 scroll-mt-20">
          <div className="mx-auto max-w-7xl rounded-3xl overflow-hidden bg-slate-900 dark:bg-black text-white relative">
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
            <div className="relative z-10 grid lg:grid-cols-2 gap-12 p-12 lg:p-20 items-center">
              <div>
                <h2 className="text-3xl lg:text-5xl font-extrabold mb-6">Teknik som bara fungerar.</h2>
                <p className="text-lg text-slate-300 mb-8 max-w-md">
                  Vi använder modern 'Edge Computing' och en blixtsnabb databas som körs direkt i din webbläsare.
                </p>
                <ul className="space-y-4">
                  {[
                    "Ingen tung installation krävs",
                    "Data laddas på millisekunder",
                    "Offline-stöd för möten utan WiFi"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                      <span className="text-lg font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-slate-800/50 rounded-2xl p-8 border border-white/10 backdrop-blur-sm">
                <div className="font-mono text-sm text-emerald-400 mb-4">terminal ~ system check</div>
                <div className="space-y-3 font-mono text-sm">
                  <div className="flex justify-between border-b border-white/10 pb-2">
                    <span className="text-slate-400">Encryption</span>
                    <span className="text-white">TLS 1.3 / AES-256</span>
                  </div>
                  <div className="flex justify-between border-b border-white/10 pb-2">
                    <span className="text-slate-400">Compliance</span>
                    <span className="text-white">GDPR / SCHREMS II</span>
                  </div>
                  <div className="flex justify-between pt-2">
                    <span className="text-slate-400">Status</span>
                    <span className="text-emerald-400 font-bold flex items-center gap-2"><div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> ONLINE</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-24 bg-slate-50 dark:bg-slate-900/30 scroll-mt-20">
          <div className="mx-auto max-w-4xl px-6">
            <h2 className="text-3xl font-extrabold text-center mb-12">Vanliga frågor</h2>
            <div className="space-y-4">
              {[
                { q: "Krävs inloggning?", a: "Nej, grundversionen använder öppen data och kräver inget konto." },
                { q: "Vilka kommuner finns?", a: "Alla Sveriges 290 kommuner finns tillgängliga i systemet." },
                { q: "Kan jag exportera grafer?", a: "Absolut. Du kan spara vyer som bilder för presentationer." },
              ].map((item, i) => (
                <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                  <h3 className="text-lg font-bold mb-2">{item.q}</h3>
                  <p className="text-slate-600 dark:text-slate-400">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BOTTOM CTA */}
        <section className="py-24 px-6 text-center">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-4xl font-extrabold mb-6">Redo att utforska din kommunstatistik?</h2>
            <p className="text-xl text-slate-600 dark:text-slate-400 mb-10">Starta analysen nu. Det är gratis, öppet och tillgängligt för alla.</p>
            <Link
              to="/kommun"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-lg hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              <PlayCircle className="h-5 w-5" />
              Starta eduInsights
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-sm">
          <div className="mx-auto max-w-7xl px-6 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white">
              <img src="/logo.svg" className="h-6 w-6 opacity-80" alt="Logo" />
              eduInsights
            </div>
            <div className="text-slate-500">
              © 2026 eduInsights. Byggd för svensk skola.
            </div>
            <div className="flex gap-6">
              <Link to="/about" className="text-slate-600 hover:text-emerald-600 transition-colors">Om oss</Link>
              <a href="#privacy" className="text-slate-600 hover:text-emerald-600 transition-colors">Integritet</a>
              <a href="#contact" className="text-slate-600 hover:text-emerald-600 transition-colors">Kontakt</a>
            </div>
          </div>
        </footer>

      </main>
    </>
  );
}
